// src/pages/projects/ManageProjectsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaLayerGroup,
  FaBuilding,
  FaCalendarAlt,
  FaInfoCircle
} from 'react-icons/fa';

import api, { fetchProjectListApi } from '../../services/api';
import MetaTags from '../../components/MetaTags';
import DataTable from '../../components/DataTable'; // ✅ Import reusable Table
import './ManageProjectPage.css';

const ManageProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // ✅ LOAD PROJECTS
  const loadProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await fetchProjectListApi();
      const mappedProjects = (data.data || []).map(p => ({
        id: p.id,
        projectName: p.project_name || p.projectName,
        projectType: p.project_type || p.projectType,
        projectDirectorName: p.director_name || p.projectDirectorName || "N/A",
        projectStartDate: p.start_date || p.projectStartDate,
        projectEndDate: p.end_date || p.projectEndDate,
        status: p.status === "active" ? "Active" : "Expired",
      }));
      setProjects(mappedProjects);
    } catch (err) {
      console.error(err);
      setError('Failed to load projects.');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // ✅ EDIT PROJECT (Modified to use URL parameter)
  const handleEdit = (projectId) => { // Renamed project_id to projectId for clarity
    if (!projectId) return alert("Project ID not found.");
    navigate(`/dashboard/post-projects/${projectId}`); // Navigate to /dashboard/post-projects/:projectId
  };

  // ✅ DELETE PROJECT
  const handleDelete = async (id) => {
    if (!id) return alert("Invalid Project ID");
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await api.delete(`/projects/${id}`);
        alert('Deleted successfully!');
        loadProjects();
      } catch (err) {
        alert('Delete failed.');
      }
    }
  };

  // ✅ DEFINE COLUMNS FOR TANSTACK TABLE
  const columns = useMemo(() => [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => <span className="text-white opacity-50">{info.row.index + 1}</span>,
      enableSorting: false, 
    },
    {
      accessorKey: 'projectName',
      header: 'Project Title',
      cell: (info) => <span className="text-white fw-bold">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectType',
      header: 'Type',
      cell: (info) => <span className="text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectDirectorName',
      header: 'Director',
      cell: (info) => <span className="text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectStartDate',
      header: 'Start Date',
      cell: (info) => <span className="small text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectEndDate',
      header: 'End Date',
      cell: (info) => <span className="small text-white opacity-75">{info.getValue()}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => (
        <Badge bg={info.getValue() === 'Expired' ? 'danger' : 'success'}>
          {info.getValue()}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      enableSorting: false, 
      cell: (info) => (
        <div className="d-flex justify-content-center gap-2">
          <Button variant="link" className="p-0 text-info" onClick={() => handleEdit(info.row.original.id)}>
            <FaEdit />
          </Button>
          <Button variant="link" className="p-0 text-danger" onClick={() => handleDelete(info.row.original.id)}>
            <FaTrash />
          </Button>
        </div>
      ),
    }
  ], []);

  // ✅ RENDER MOBILE CARD
  const renderMobileCard = (project) => (
    <div className="project-mobile-card mb-2" key={project.id}>
      <div className="d-flex justify-content-between align-items-start mb-1">
        <h6 className="project-card-title m-0">{project.projectName}</h6>
        <Badge bg={project.status === 'Expired' ? 'danger' : 'success'}>
          {project.status}
        </Badge>
      </div>
      <div className="card-data-grid mt-2">
        <div className="data-item">
          <FaLayerGroup className="text-danger" />
          <span className="lbl">Type:</span>
          <span className="val">{project.projectType}</span>
        </div>
        <div className="data-item">
          <FaInfoCircle className="text-info" />
          <span className="lbl">Status:</span>
          <span className="val">{project.status}</span>
        </div>
        <div className="data-item">
          <FaCalendarAlt className="text-success" />
          <span className="lbl">Start:</span>
          <span className="val">{project.projectStartDate}</span>
        </div>
        <div className="data-item">
          <FaCalendarAlt className="text-warning" />
          <span className="lbl">End:</span>
          <span className="val">{project.projectEndDate}</span>
        </div>
      </div>
      <hr className="compact-hr" />
      <div className="d-flex justify-content-between align-items-center">
        <div className="house-data">
          <FaBuilding className="text-info me-1" style={{ fontSize: '10px' }} />
          <span className="house-name-gold">{project.projectDirectorName}</span>
        </div>
        <div className="d-flex gap-4">
          <FaEdit className="text-info action-icon" onClick={() => handleEdit(project.id)} style={{ cursor: 'pointer' }} />
          <FaTrash className="text-danger action-icon" onClick={() => handleDelete(project.id)} style={{ cursor: 'pointer' }} />
        </div>
      </div>
    </div>
  );

  // ✅ LOADING VIEW
  if (loading) {
    return (
      <div className="text-center p-5 vh-100" >
        <Spinner animation="border" variant="danger" />
        <p className="text-white mt-2">Project Loading...</p>
      </div>
    );
  }

  // ✅ MAIN RENDER
  return (
    <div className="post-project-page p-2 p-md-3 animate-fade-in">
      <MetaTags title="Manage Projects - Dashboard" />

      {/* HEADER */}
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          Manage <span style={{ color: 'var(--primary-color)' }}>Projects</span>
        </h5>
        
        <Button
          as={Link}
          to="/dashboard/post-projects"
          variant="danger"
          className="btn-compact-header btn-sm fw-bold"
        >
          <FaPlus className="me-1" /> New Project
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">
          {error}
        </Alert>
      )}

      {/* REUSABLE TANSTACK DATA TABLE */}
      <DataTable 
        columns={columns} 
        data={projects} 
        renderMobileItem={renderMobileCard} 
        searchPlaceholder="Search projects or directors..." 
        className="form-control bg-dark text-white border-secondary w-100 w-md-auto"
      />

    </div>
  );
};

export default ManageProjectsPage;